import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import { Bell, Save, RefreshCw } from 'lucide-react';
import { API_URL } from './config';

// Admin UI for the SMS/push nearby-driver notification knobs — added 2026-09-10 alongside the
// backend fix wiring these into GET/POST /control-panel/settings/notification-settings
// (routes/control_panel.js). Nothing exposed these settings anywhere in the dashboard before this;
// they previously required editing the DB directly to change.
const DEFAULT_SETTINGS = {
  private_ride_sms: { radius_km: 5.0, closest_drivers: 3 },
  rideshare_request_notify: { radius_km: 5.0, closest_drivers: 5 },
  regular_delivery_sms: { radius_km: 5.0, closest_drivers: 5 },
  sms_min_drivers_threshold: 10,
};

const NotificationSettingsControl = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/control-panel/settings/notification-settings`);
      const json = await res.json();
      if (json.success && json.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...json.data });
      } else {
        setAlert({ show: true, type: 'error', message: 'Failed to load notification settings.' });
      }
    } catch (err) {
      setAlert({ show: true, type: 'error', message: `Failed to load notification settings: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const updateField = (section, field, value) => {
    setSettings((prev) =>
      section
        ? { ...prev, [section]: { ...prev[section], [field]: value } }
        : { ...prev, [field]: value }
    );
  };

  const saveSettings = async () => {
    setSaving(true);
    setAlert({ show: false, type: '', message: '' });
    try {
      const res = await fetch(`${API_URL}/control-panel/settings/notification-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      const json = await res.json();
      if (json.success) {
        setSettings({ ...DEFAULT_SETTINGS, ...json.data });
        setAlert({ show: true, type: 'success', message: 'Notification settings saved.' });
      } else {
        setAlert({ show: true, type: 'error', message: json.message || 'Failed to save settings.' });
      }
    } catch (err) {
      setAlert({ show: true, type: 'error', message: `Failed to save settings: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Bell size={22} />
        <Typography variant="h5" fontWeight={700}>
          Notification Settings
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Controls how nearby drivers are alerted about new rides — push notification radius/count,
        and when an SMS backup is also sent (only when few drivers are available nearby, to control
        SMS cost).
      </Typography>

      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, show: false })}>
          {alert.message}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            SMS backup threshold
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Shared by both private-ride and rideshare notifications. SMS is only sent when fewer
            than this many drivers are available nearby — push alone is assumed sufficient
            otherwise.
          </Typography>
          <TextField
            label="Minimum drivers before SMS backup sends"
            type="number"
            size="small"
            value={settings.sms_min_drivers_threshold}
            onChange={(e) => updateField(null, 'sms_min_drivers_threshold', e.target.value)}
            inputProps={{ min: 1, max: 100 }}
            sx={{ width: 320 }}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Private ride
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Radius (km)"
                type="number"
                size="small"
                fullWidth
                value={settings.private_ride_sms.radius_km}
                onChange={(e) => updateField('private_ride_sms', 'radius_km', e.target.value)}
                inputProps={{ min: 0.5, max: 50, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Closest drivers to notify"
                type="number"
                size="small"
                fullWidth
                value={settings.private_ride_sms.closest_drivers}
                onChange={(e) => updateField('private_ride_sms', 'closest_drivers', e.target.value)}
                inputProps={{ min: 1, max: 50 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Rideshare
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Radius (km)"
                type="number"
                size="small"
                fullWidth
                value={settings.rideshare_request_notify.radius_km}
                onChange={(e) => updateField('rideshare_request_notify', 'radius_km', e.target.value)}
                inputProps={{ min: 0.5, max: 50, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Closest drivers to notify"
                type="number"
                size="small"
                fullWidth
                value={settings.rideshare_request_notify.closest_drivers}
                onChange={(e) => updateField('rideshare_request_notify', 'closest_drivers', e.target.value)}
                inputProps={{ min: 1, max: 50 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button variant="contained" startIcon={<Save size={16} />} onClick={saveSettings} disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save Settings'}
        </Button>
        <Button variant="outlined" startIcon={<RefreshCw size={16} />} onClick={fetchSettings} disabled={saving || loading}>
          Reload
        </Button>
      </Box>
    </Box>
  );
};

export default NotificationSettingsControl;
